// © 2025 Camp Promethean, LLC. All Rights Reserved.
//
// HandsFreeEntry — embeddable Hands-Free capture unit (T-206).
//
// Bundles the three input modalities (voice / type / photo) plus the inline
// ReviewCard into one self-contained component a logger page can drop in, e.g.:
//
//   <HandsFreeEntry workflow="runoff_log" onCommitted={() => refetchLogs()} />
//
// When `workflow` is provided the modality selectors are hidden and every entry
// is pinned to that workflow — so a runoff logger page only ever produces runoff
// review items. Omit `workflow` to let each modality offer Auto-detect.
//
// This is the reusable building block; wiring it into specific logger pages
// (runoff, yield, diary) is the remaining tracked step of T-206.

import { useCallback, useState } from "react";
import { Camera, Keyboard, Mic } from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { hfDark, hfTabsList } from "./handsFreeUi";
import { cn } from "@/lib/utils";
import { Recorder } from "./Recorder";
import { QuickLog } from "./QuickLog";
import { PhotoLog } from "./PhotoLog";
import { ReviewCard } from "./ReviewCard";
import type { HandsFreeContext, HfReviewItem, PipelineResult, WorkflowType } from "./types";

interface HandsFreeEntryProps {
  /** Pin every entry to this workflow and hide the modality workflow selectors. */
  workflow?: WorkflowType;
  /** Page-context pins forwarded to ingest (e.g. crop_id from route). */
  context?: HandsFreeContext;
  /** Called with the committed production-row id after a successful commit. */
  onCommitted?: (committedRecordId: string) => void;
  /** Which input modalities to offer. Defaults to all three. */
  modalities?: Array<"record" | "type" | "photo">;
}

export function HandsFreeEntry({
  workflow,
  context,
  onCommitted,
  modalities = ["record", "type", "photo"],
}: HandsFreeEntryProps) {
  const [activeReviewItemId, setActiveReviewItemId] = useState<string | null>(null);
  const [pipelineResult, setPipelineResult] = useState<PipelineResult | null>(null);
  const [tab, setTab] = useState<string>(modalities[0] ?? "record");

  const handlePipelineResult = useCallback((result: PipelineResult) => {
    if (result.review_item_id) {
      setActiveReviewItemId(result.review_item_id);
      setPipelineResult(result);
    }
  }, []);

  const handleReset = useCallback(() => {
    setActiveReviewItemId(null);
    setPipelineResult(null);
  }, []);

  const initialData: HfReviewItem | undefined =
    pipelineResult?.review_item_id && pipelineResult.proposed_data && pipelineResult.status
      ? ({
          id: pipelineResult.review_item_id,
          workflow_type: pipelineResult.workflow_type ?? workflow ?? "runoff_log",
          status: pipelineResult.status as HfReviewItem["status"],
          proposed_data: pipelineResult.proposed_data,
          resolution_summary: pipelineResult.resolution_summary ?? null,
          committed_record_id: null,
          committed_table: null,
          reviewed_at: null,
          reviewed_by: null,
          created_at: new Date().toISOString(),
          facility_id: pipelineResult.proposed_data.facility_id,
          transcript_id: pipelineResult.transcript_id,
        } satisfies HfReviewItem)
      : undefined;

  const showTabs = modalities.length > 1;

  const renderModality = (m: "record" | "type" | "photo") => {
    const common = {
      onPipelineResult: handlePipelineResult,
      onReset: handleReset,
      fixedWorkflow: workflow,
      context,
    };
    if (m === "record") return <Recorder {...common} />;
    if (m === "type") return <QuickLog {...common} />;
    return <PhotoLog {...common} />;
  };

  return (
    <div className={cn(hfDark, "rounded-lg border border-border/50 p-4")}>
      <div className="space-y-4">
      {showTabs ? (
        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className={cn(hfTabsList, "grid w-full")} style={{ gridTemplateColumns: `repeat(${modalities.length}, minmax(0, 1fr))` }}>
            {modalities.includes("record") && (
              <TabsTrigger value="record" className="flex-1 gap-1.5">
                <Mic className="h-3.5 w-3.5" /> Record
              </TabsTrigger>
            )}
            {modalities.includes("type") && (
              <TabsTrigger value="type" className="flex-1 gap-1.5">
                <Keyboard className="h-3.5 w-3.5" /> Type
              </TabsTrigger>
            )}
            {modalities.includes("photo") && (
              <TabsTrigger value="photo" className="flex-1 gap-1.5">
                <Camera className="h-3.5 w-3.5" /> Photo
              </TabsTrigger>
            )}
          </TabsList>
          {modalities.map((m) => (
            <TabsContent key={m} value={m} className="mt-4">
              {renderModality(m)}
            </TabsContent>
          ))}
        </Tabs>
      ) : (
        renderModality(modalities[0] ?? "record")
      )}

      {activeReviewItemId && (
        <ReviewCard
          reviewItemId={activeReviewItemId}
          initialData={initialData}
          onCommitted={onCommitted}
          onRejected={handleReset}
        />
      )}
      </div>
    </div>
  );
}

export default HandsFreeEntry;
